import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, FileUp } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { Trans } from 'react-i18next';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import {
  DATE_FORMATS,
  autoMapStatement,
  bankingKey,
  guessDateFormat,
  mappedRows,
  useBankAccounts,
  type BankAccount,
  type CsvField,
  type CsvOptions,
  type DateFormat,
  type ImportResult,
} from '@/lib/banking';
import { checkStatementMapping, statementFileName } from '@/lib/banking-forms';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';
import { parseCsv, type ParsedCsv } from '@/lib/imports';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';
import { useAccountNames } from '@/lib/accounts';

export const Route = createFileRoute('/banking/import')({
  component: ImportStatementPage,
  validateSearch: (s: Record<string, unknown>): { accountId?: string } =>
    typeof s['accountId'] === 'string' ? { accountId: s['accountId'] } : {},
});

type Step = 'upload' | 'map' | 'review' | 'done';
const STEPS: Array<{ key: Step }> = [
  { key: 'upload' },
  { key: 'map' },
  { key: 'review' },
  { key: 'done' },
];
const MAX_ROWS = 5000;

type Source =
  | { format: 'csv'; name: string; csv: ParsedCsv }
  | { format: 'ofx'; name: string; content: string };

/** Screen B2 (document 06): Import statement (CSV or OFX). */
function ImportStatementPage() {
  const orgId = useOrgId();
  const me = useMe();
  const banks = useBankAccounts();
  const { accountId } = Route.useSearch();
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('purchasePages.import.title')}
        description={t('purchasePages.import.description')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/banking" search={accountId ? { accountId } : {}}>
              {t('purchasePages.import.back')}
            </Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState
          title={t('purchasePages.import.staffOnly')}
          hint={t('purchasePages.import.staffOnlyHint')}
        />
      ) : banks.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : banks.isError ? (
        <EmptyState title={t('purchasePages.import.loadFailed')} hint={banks.error.message} />
      ) : banks.data.length === 0 ? (
        <EmptyState
          title={t('purchasePages.import.noBanks')}
          hint={t('purchasePages.import.noBanksHint')}
        />
      ) : (
        <Wizard key={orgId} banks={banks.data} initialAccountId={accountId ?? null} />
      )}
    </>
  );
}

function Wizard({
  banks,
  initialAccountId,
}: {
  banks: BankAccount[];
  initialAccountId: string | null;
}) {
  const orgId = useOrgId();
  const t = useT();
  const names = useAccountNames();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('upload');
  const [accountId, setAccountId] = useState(
    banks.find((b) => b.accountId === initialAccountId)?.accountId ?? banks[0]!.accountId,
  );
  const bank = banks.find((b) => b.accountId === accountId)!;
  const [source, setSource] = useState<Source | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<CsvField, string>>>({});
  const [options, setOptions] = useState<CsvOptions>({
    dateFormat: 'dd/MM/yyyy',
    amountMode: 'single',
    positive: 'money_in',
  });
  const [onlyProblems, setOnlyProblems] = useState(false);

  const payload = () =>
    source?.format === 'ofx'
      ? {
          format: 'ofx',
          accountId,
          fileName: statementFileName(source.name),
          content: source.content,
        }
      : {
          format: 'csv',
          accountId,
          fileName: statementFileName(source!.name),
          rows: mappedRows((source as { csv: ParsedCsv }).csv, mapping, options.amountMode),
          options,
          mapping,
        };

  const preview = useMutation({
    mutationFn: (p: object) =>
      api<ImportResult>(`/orgs/${orgId}/banking/imports/preview`, {
        method: 'POST',
        body: JSON.stringify(p),
      }),
    onSuccess: () => {
      setOnlyProblems(false);
      setStep('review');
    },
  });
  const run = useMutation({
    mutationFn: () =>
      api<ImportResult>(`/orgs/${orgId}/banking/imports`, {
        method: 'POST',
        body: JSON.stringify(payload()),
      }),
    onSuccess: async () => {
      setStep('done');
      await Promise.all([
        qc.invalidateQueries({ queryKey: bankingKey(orgId) }),
        qc.invalidateQueries({ queryKey: ['accounts', orgId] }),
      ]);
    },
  });

  async function choose(f: File | undefined) {
    setFileError(null);
    preview.reset();
    if (!f) return;
    try {
      const text = await f.text();
      if (/\.(ofx|qfx)$/i.test(f.name) || /<OFX>/i.test(text.slice(0, 4000))) {
        const s: Source = { format: 'ofx', name: f.name, content: text };
        setSource(s);
        preview.mutate({
          format: 'ofx',
          accountId,
          fileName: statementFileName(f.name),
          content: text,
        });
        return;
      }
      if (!/\.(csv|txt)$/i.test(f.name) && f.type !== 'text/csv')
        throw new Error(t('purchasePages.import.wrongFile'));
      const csv = await parseCsv(f);
      if (!csv.rows.length) throw new Error(t('purchasePages.import.noRows'));
      if (csv.rows.length > MAX_ROWS)
        throw new Error(
          t('purchasePages.import.tooManyRows', {
            rows: csv.rows.length.toLocaleString('en-GB'),
            max: MAX_ROWS.toLocaleString('en-GB'),
          }),
        );
      const auto = autoMapStatement(csv.headers, bank.importMapping);
      const dateCol = auto.mapping.date ? csv.headers.indexOf(auto.mapping.date) : -1;
      const guessed = dateCol >= 0 ? guessDateFormat(csv.rows.map((r) => r[dateCol] ?? '')) : null;
      setSource({ format: 'csv', name: f.name, csv });
      setMapping(auto.mapping);
      setOptions({
        dateFormat: bank.importMapping?.options.dateFormat ?? guessed ?? 'dd/MM/yyyy',
        amountMode: auto.amountMode,
        positive:
          bank.importMapping?.options.positive ??
          (bank.accountType === 'credit_card' ? 'money_out' : 'money_in'),
      });
      setStep('map');
    } catch (e) {
      setFileError((e as Error).message);
    }
  }

  function reset() {
    setSource(null);
    setMapping({});
    preview.reset();
    run.reset();
    setStep('upload');
  }

  const result = step === 'done' ? run.data : preview.data;

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <Stepper step={step} skipMap={source?.format === 'ofx'} />
      {step === 'upload' ? (
        <>
          <ErrorBanner>{fileError ?? preview.error?.message ?? null}</ErrorBanner>
          <div className="max-w-md">
            <Field label={t('purchasePages.import.bankAccount')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  {banks.map((b) => (
                    <option key={b.accountId} value={b.accountId}>
                      {b.code} · {names.name(b.accountId, b.name)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <DropZone busy={preview.isPending} onFile={choose} />
        </>
      ) : null}
      {step === 'map' && source?.format === 'csv' ? (
        <MapStep
          csv={source.csv}
          name={source.name}
          bank={bank}
          mapping={mapping}
          setMapping={setMapping}
          options={options}
          setOptions={setOptions}
          error={preview.error?.message ?? null}
          busy={preview.isPending}
          onBack={reset}
          onNext={() => preview.mutate(payload())}
        />
      ) : null}
      {(step === 'review' || step === 'done') && result ? (
        <ReviewStep
          result={result}
          bank={bank}
          done={step === 'done'}
          onlyProblems={onlyProblems}
          setOnlyProblems={setOnlyProblems}
          error={run.error?.message ?? null}
          busy={run.isPending}
          onBack={() => (source?.format === 'csv' ? setStep('map') : reset())}
          onImport={() => run.mutate()}
          onAnother={reset}
        />
      ) : null}
    </div>
  );
}

function Stepper({ step, skipMap }: { step: Step; skipMap: boolean }) {
  const steps = skipMap ? STEPS.filter((s) => s.key !== 'map') : STEPS;
  const at = steps.findIndex((s) => s.key === step);
  const t = useT();
  return (
    <ol
      className="flex flex-wrap items-center gap-2 text-sm"
      aria-label={t('purchasePages.import.stepsLabel')}
    >
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2">
          <span
            className={cn(
              'flex size-6 items-center justify-center rounded-full border text-xs',
              i < at && 'border-primary bg-primary text-primary-foreground',
              i === at && 'border-primary text-primary',
              i > at && 'text-muted-foreground',
            )}
          >
            {i < at ? <Check className="size-3.5" /> : i + 1}
          </span>
          <span
            className={cn(i === at ? 'font-medium' : 'text-muted-foreground')}
            aria-current={i === at ? 'step' : undefined}
          >
            {t(`purchasePages.import.steps.${s.key}`)}
          </span>
          {i < steps.length - 1 ? <span className="mx-1 h-px w-8 bg-border" /> : null}
        </li>
      ))}
    </ol>
  );
}

function DropZone({ busy, onFile }: { busy: boolean; onFile: (f: File | undefined) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const t = useT();
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    onFile(e.dataTransfer.files[0]);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed bg-card px-6 py-10 text-center',
        over && 'border-primary bg-primary/5',
      )}
    >
      <FileUp className="size-8 text-primary" />
      <div>
        <p className="font-medium">{t('purchasePages.import.dropTitle')}</p>
        <p className="text-sm text-muted-foreground">
          {t('purchasePages.import.dropHint', { max: MAX_ROWS.toLocaleString('en-GB') })}
        </p>
      </div>
      <Button disabled={busy} onClick={() => input.current?.click()}>
        {busy ? t('purchasePages.import.reading') : t('purchasePages.import.browse')}
      </Button>
      <input
        ref={input}
        type="file"
        accept=".csv,.txt,.ofx,.qfx,text/csv"
        className="sr-only"
        aria-label={t('purchasePages.import.fileLabel')}
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function MapStep({
  csv,
  name,
  bank,
  mapping,
  setMapping,
  options,
  setOptions,
  error,
  busy,
  onBack,
  onNext,
}: {
  csv: ParsedCsv;
  name: string;
  bank: BankAccount;
  mapping: Partial<Record<CsvField, string>>;
  setMapping: (m: Partial<Record<CsvField, string>>) => void;
  options: CsvOptions;
  setOptions: (o: CsvOptions) => void;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const [tried, setTried] = useState(false);
  const t = useT();
  const names = useAccountNames();
  const label = (f: CsvField) => t(`purchasePages.import.fields.${f}`);
  const fields: Array<{ key: CsvField; required?: boolean; hint?: string }> = [
    { key: 'date', required: true },
    {
      key: 'description',
      required: !mapping.payee,
      hint: t('purchasePages.import.hintDescription'),
    },
    { key: 'payee' },
    { key: 'reference', hint: t('purchasePages.import.hintReference') },
    ...(options.amountMode === 'single'
      ? [{ key: 'amount' as const, required: true }]
      : [
          { key: 'moneyIn' as const, hint: t('purchasePages.import.hintMoneyIn') },
          { key: 'moneyOut' as const, hint: t('purchasePages.import.hintMoneyOut') },
        ]),
    { key: 'balance', hint: t('purchasePages.import.hintBalance') },
  ];
  const first = csv.rows[0] ?? [];
  const check = checkStatementMapping(mapping, options.amountMode);
  const twice = new Set(check.twice);
  const missing = check.missing;
  const splitMissing = check.splitMissing;
  const blocked = !check.ok;
  const set = (key: CsvField, header: string) => {
    const next = { ...mapping };
    if (header) next[key] = header;
    else delete next[key];
    setMapping(next);
  };
  return (
    <>
      <ErrorBanner>
        {error ??
          (tried && missing.length
            ? t('purchasePages.import.mapMissing', {
                fields: missing.map(label).join(t('purchasePages.import.listSeparator')),
              })
            : tried && splitMissing
              ? t('purchasePages.import.mapSplitMissing')
              : tried && twice.size
                ? t('purchasePages.import.mapTwice', {
                    columns: [...twice].join(t('purchasePages.import.listSeparator')),
                  })
                : null)}
      </ErrorBanner>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">
          <bdi>{name}</bdi>
        </span>{' '}
        ·{' '}
        <Trans
          t={t}
          i18nKey={
            bank.importMapping
              ? 'purchasePages.import.mapIntroSaved'
              : 'purchasePages.import.mapIntroAuto'
          }
          count={csv.rows.length}
          values={{
            n: csv.rows.length.toLocaleString('en-GB'),
            bank: names.name(bank.accountId, bank.name),
          }}
          components={{ b: <bdi className="font-medium text-foreground" /> }}
        />
      </p>
      <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 md:grid-cols-3">
        <Field label={t('purchasePages.import.dateFormat')}>
          {(id) => (
            <NativeSelect
              id={id}
              value={options.dateFormat}
              onChange={(e) => setOptions({ ...options, dateFormat: e.target.value as DateFormat })}
            >
              {DATE_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('purchasePages.import.amountsIn')}>
          {(id) => (
            <NativeSelect
              id={id}
              value={options.amountMode}
              onChange={(e) =>
                setOptions({ ...options, amountMode: e.target.value as CsvOptions['amountMode'] })
              }
            >
              <option value="single">{t('purchasePages.import.oneColumn')}</option>
              <option value="split">{t('purchasePages.import.twoColumns')}</option>
            </NativeSelect>
          )}
        </Field>
        {options.amountMode === 'single' ? (
          <Field label={t('purchasePages.import.positiveAre')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={options.positive}
                onChange={(e) =>
                  setOptions({ ...options, positive: e.target.value as CsvOptions['positive'] })
                }
              >
                <option value="money_in">{t('purchasePages.import.positiveIn')}</option>
                <option value="money_out">{t('purchasePages.import.positiveOut')}</option>
              </NativeSelect>
            )}
          </Field>
        ) : null}
      </div>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[240px]">{t('purchasePages.import.colField')}</TableHead>
              <TableHead className="w-[280px]">{t('purchasePages.import.colColumn')}</TableHead>
              <TableHead>{t('purchasePages.import.colFirstRow')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((f) => {
              const header = mapping[f.key] ?? '';
              const i = csv.headers.indexOf(header);
              const bad = (tried && missing.includes(f.key)) || (header && twice.has(header));
              return (
                <TableRow key={f.key}>
                  <TableCell>
                    <div className="font-medium">
                      {label(f.key)}
                      {f.required ? ' *' : ''}
                    </div>
                    {f.hint ? <div className="text-xs text-muted-foreground">{f.hint}</div> : null}
                  </TableCell>
                  <TableCell>
                    <NativeSelect
                      aria-label={t('purchasePages.import.columnFor', { field: label(f.key) })}
                      aria-invalid={Boolean(bad)}
                      value={header}
                      onChange={(e) => set(f.key, e.target.value)}
                    >
                      <option value="">{t('purchasePages.import.notMapped')}</option>
                      {csv.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </NativeSelect>
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate text-sm text-muted-foreground">
                    {i >= 0 ? (first[i] ?? '') : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          {t('purchasePages.import.back2')}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            setTried(true);
            if (!blocked) onNext();
          }}
        >
          {busy ? t('purchasePages.import.checking') : t('purchasePages.import.next')}
        </Button>
      </div>
    </>
  );
}

function ReviewStep({
  result,
  bank,
  done,
  onlyProblems,
  setOnlyProblems,
  error,
  busy,
  onBack,
  onImport,
  onAnother,
}: {
  result: ImportResult;
  bank: BankAccount;
  done: boolean;
  onlyProblems: boolean;
  setOnlyProblems: (v: boolean) => void;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onImport: () => void;
  onAnother: () => void;
}) {
  const rows = onlyProblems ? result.rows.filter((r) => r.status !== 'ok') : result.rows;
  const n = (v: number) => v.toLocaleString('en-GB');
  const t = useT();
  const names = useAccountNames();
  const c = (count: number) => ({ count, n: n(count) });
  const fieldLabel = (f: string) =>
    (
      [
        'date',
        'description',
        'payee',
        'reference',
        'amount',
        'moneyIn',
        'moneyOut',
        'balance',
      ] as string[]
    ).includes(f)
      ? t(`purchasePages.import.fields.${f as CsvField}`)
      : t('purchasePages.import.transaction');
  return (
    <>
      <ErrorBanner>{error}</ErrorBanner>
      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-sm',
          done ? 'border-primary/30 bg-primary/5' : 'bg-card',
        )}
        role="status"
      >
        {done ? (
          <>
            <span className="font-medium">
              {t('purchasePages.import.imported', {
                ...c(result.imported ?? 0),
                bank: names.name(bank.accountId, bank.name),
              })}
            </span>{' '}
            {result.autoAdded ? `${t('purchasePages.import.autoAdded', c(result.autoAdded))} ` : ''}
            {result.ruleSuggested
              ? `${t('purchasePages.import.ruleSuggested', c(result.ruleSuggested))} `
              : ''}
            {result.duplicates
              ? `${t('purchasePages.import.duplicatesSkipped', c(result.duplicates))} `
              : ''}
            {result.errors ? t('purchasePages.import.errorsSkipped', c(result.errors)) : ''}
          </>
        ) : (
          <>
            <span className="font-medium">
              {t('purchasePages.import.ready', { ...c(result.total), ok: n(result.ok) })}
            </span>{' '}
            {result.duplicates
              ? `${t('purchasePages.import.duplicatesWillSkip', c(result.duplicates))} `
              : ''}
            {result.errors ? t('purchasePages.import.errorsWillSkip', c(result.errors)) : ''}
          </>
        )}
        {result.statementBalance ? (
          <div className="mt-1 text-muted-foreground">
            {t('purchasePages.import.statementBalance', {
              amount: ltr(`${bank.currency} ${formatAmount(result.statementBalance.amount)}`),
              date: ltr(displayDate(result.statementBalance.date)),
            })}
          </div>
        ) : null}
      </div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={onlyProblems}
          onChange={(e) => setOnlyProblems(e.target.checked)}
        />
        {t('purchasePages.import.onlyProblems')}
      </label>
      <div className="max-h-[480px] overflow-y-auto rounded-lg border bg-card">
        <Table>
          <TableHeader className="sticky top-0 bg-card">
            <TableRow>
              <TableHead className="w-[70px] text-end">
                {t('purchasePages.import.colRow')}
              </TableHead>
              <TableHead className="w-[110px]">{t('purchasePages.import.colDate')}</TableHead>
              <TableHead>{t('purchasePages.import.colDescription')}</TableHead>
              <TableHead className="w-[140px] text-end">
                {t('purchasePages.import.colAmount')}
              </TableHead>
              <TableHead className="w-[120px]">{t('purchasePages.import.colStatus')}</TableHead>
              <TableHead>{t('purchasePages.import.colErrors')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.row}>
                <TableCell className="tnum text-end text-muted-foreground">{r.row}</TableCell>
                <TableCell className="tnum">{r.date ? displayDate(r.date) : ''}</TableCell>
                <TableCell>{r.description ?? ''}</TableCell>
                <TableCell className="tnum text-end">{formatAmount(r.amount)}</TableCell>
                <TableCell>
                  {r.status === 'ok' ? (
                    <Badge variant="secondary">
                      {done
                        ? t('purchasePages.import.statusImported')
                        : t('purchasePages.import.statusReady')}
                    </Badge>
                  ) : r.status === 'duplicate' ? (
                    <Badge variant="outline">{t('purchasePages.import.statusDuplicate')}</Badge>
                  ) : (
                    <Badge variant="destructive">{t('purchasePages.import.statusError')}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {r.errors.map((e, i) => (
                    <div key={i} className="text-destructive">
                      <span className="font-medium">
                        {t('purchasePages.import.errorField', { field: fieldLabel(e.field) })}
                      </span>
                      {e.message}
                    </div>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        {done ? (
          <>
            <Button variant="outline" onClick={onAnother}>
              {t('purchasePages.import.another')}
            </Button>
            <Button asChild>
              <Link to="/banking" search={{ accountId: bank.accountId, tab: 'for_review' }}>
                {t('purchasePages.import.reviewTransactions')}
              </Link>
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onBack}>
              {t('purchasePages.import.back2')}
            </Button>
            <Button disabled={busy || result.ok === 0} onClick={onImport}>
              {busy
                ? t('purchasePages.import.importing')
                : t('purchasePages.import.importN', c(result.ok))}
            </Button>
          </>
        )}
      </div>
    </>
  );
}
